import { Controller } from "@hotwired/stimulus"
import Choices from "choices.js";

// Connects to data-controller="performances--table"
export default class extends Controller {
  static targets = ["dayRow", "vacationPatternSelect"]
  static outlets = ["performances--performance-field"]
  static values = {
    vacationPatterns: Array
  }

  connect() {
    this.choicesMap = new Map()
    this.summaryTotals = this.emptySummaryTotals()

    this.dayRowTargets.forEach((row) => {
      const selectedPattern = this.getSelectedPatternForRow(row)
      if (!selectedPattern) {
        this.storeRowMetrics(row, this.emptySummaryTotals())
        return
      }
      this.calculateWorkTimeForRow(row, selectedPattern, { renderSummary: false })
    })

    this.renderSummary()
  }

  disconnect() {
    this.choicesMap.forEach((choices) => choices.destroy())
    this.choicesMap.clear()
  }

  performancesPerformanceFieldOutletConnected(_outlet, element) {
    if (!element) return
    if (this.choicesMap.has(element)) return
    this.initializeChoices(element)
  }

  performancesPerformanceFieldOutletDisconnected(_outlet, element) {
    if (!element) return
    const choices = this.choicesMap.get(element)
    if (choices) {
      choices.destroy()
      this.choicesMap.delete(element)
    }
  }


  togleActiveRow(event) {
    this.dayRowTargets.forEach(row => row.classList.remove("selected-row"))
    event.currentTarget.classList.add("selected-row")
  }

  updateVacationPattern(event) {
    const selectedPatternId = event.target.value
    const row = event.target.closest("tr.day-row")
    const selectedPattern = this.vacationPatternsValue.find(pattern => pattern.id === parseInt(selectedPatternId))
    if (selectedPattern) {
      this.setDefaultPerformancesData(row, selectedPattern)
      this.calculateWorkTimeForRow(row, selectedPattern)
    }
  }

  onTimeInputChange(event) {
    const row = event.target.closest("tr.day-row")
    const vacationSelect = row.querySelector(".vacation-pattern-select")
    const selectedPatternId = vacationSelect.value
    const selectedPattern = this.vacationPatternsValue.find(pattern => pattern.id === parseInt(selectedPatternId))
    if (selectedPattern) this.calculateWorkTimeForRow(row, selectedPattern)
  }

  setDefaultPerformancesData(row, vacationPattern) {
    const { project_part: projectPart, time_slice: timeSlice } = vacationPattern
    const options = row.querySelector(".site-time-slice").options
    this.setValueForFields(row, ".site-time-slice", () => {
      for (let i = 0; i < options.length; i++) {
        const option = options[i]
        if (option.dataset.timeSlice) {
          const optionTimeSlice = JSON.parse(option.dataset.timeSlice)
          if (optionTimeSlice.name === timeSlice.name) {
            return option.value
          }
        }
      }
      return ""
    })
    this.setValueForFields(row, ".slice-start", () => timeSlice.start_time)
    this.setValueForFields(row, ".slice-end", () => timeSlice.end_time)
    this.setValueForFields(row, ".work-man-days", () => timeSlice.man_days)
    this.setValueForFields(row, ".work-time-span", () => this.toHHMM(timeSlice.duration))
    this.setValueForFields(row, ".project-part", () => projectPart || "")
    row.querySelectorAll(".tool").forEach(el => {
      if (!el.value || el.value === "") return
      const wrapper = el.closest(".performances-field")
      if (el.value) {
        this.setValueForFields(wrapper, ".tool-time-span", () => this.toHHMM(timeSlice.duration))
        this.setValueForFields(wrapper, ".tool-man-days", () => timeSlice.man_days)
      }
    })
  }

  calculateWorkTimeForRow(row, vacationPattern, options = {}) {
    const previous = this.getRowMetrics(row)

    const showedUpAt = row.querySelector(".showed-up-at").value
    const leftAt = row.querySelector(".left-at").value
    let result = { total: 0, overtime: 0, night: 0, holiday: 0 }

    if (showedUpAt && leftAt) {
      const isHoliday = row.querySelector(".is-holiday").checked
      const normalDuration = vacationPattern.time_slice.duration
      const { break_times: breakTimes } = vacationPattern.time_slice.attendance_rule
      result = this.calculateOneDay({ startTime: showedUpAt, endTime: leftAt, isHoliday, normalWorkMinutes: normalDuration, breakTimes })
    }

    const ptoTime = this.getPaidTimeOffMinutes(vacationPattern)
    const ptoDays = this.getPaidTimeOffDays(ptoTime, vacationPattern)
    const next = {
      total: result.total,
      overtime: result.overtime,
      night: result.night,
      holiday: result.holiday,
      ptoTime,
      ptoDays,
      weeklyOvertime: 0 // placeholder
    }
    next.weeklyOvertime = this.calculateWeeklyOvertime(row, next.total)


    this.updateTableDataText(result, row)
    this.updateWorkingTimeInput(result, row)
    this.applySummaryDelta(previous, next)
    this.storeRowMetrics(row, next)

    if (options.renderSummary !== false) this.renderSummary()
  }

  getSelectedPatternForRow(row) {
    const vacationSelect = row.querySelector(".vacation-pattern-select")
    if (!vacationSelect) return null
    const selectedPatternId = vacationSelect.value
    return this.vacationPatternsValue.find(pattern => pattern.id === parseInt(selectedPatternId))
  }

  updateTableDataText(result, row) {
    row.querySelector(".td-act").textContent = this.formatTime(result.total)
    row.querySelector(".td-over").textContent = this.formatTime(result.overtime)
    row.querySelector(".td-night").textContent = this.formatTime(result.night)
    row.querySelector(".td-hol").textContent = this.formatTime(result.holiday)
  }

  setValueForFields(row, selector, valueCallback) {
    row.querySelectorAll(selector).forEach((el) => {
      const value = valueCallback(el)
      const choices = this.choicesMap.get(el)
      if (choices) {
        choices.setChoiceByValue(String(value))
      } else {
        el.value = value
      }
    })
  }

  updateWorkingTimeInput(result, row) {
    const workingTimeInput = row.querySelector(`[name*="[total_work_time]"]`)
    const nightWorkingTimeInput = row.querySelector(`[name*="[night_work_time]"]`)
    const holidayWorkingTimeInput = row.querySelector(`[name*="[holiday_work_time]"]`)
    const overtimeWorkingTimeInput = row.querySelector(`[name*="[overtime_work_time]"]`)

    if (workingTimeInput) workingTimeInput.value = result.total
    if (nightWorkingTimeInput) nightWorkingTimeInput.value = result.night
    if (holidayWorkingTimeInput) holidayWorkingTimeInput.value = result.holiday
    if (overtimeWorkingTimeInput) overtimeWorkingTimeInput.value = result.overtime
  }

  emptySummaryTotals() {
    return { total: 0, overtime: 0, night: 0, holiday: 0, ptoTime: 0, ptoDays: 0, weeklyOvertime: 0 }
  }

  getRowMetrics(row) {
    return {
      total: this.toNumber(row.dataset.total),
      overtime: this.toNumber(row.dataset.overtime),
      night: this.toNumber(row.dataset.night),
      holiday: this.toNumber(row.dataset.holiday),
      ptoTime: this.toNumber(row.dataset.ptoTime),
      ptoDays: this.toNumber(row.dataset.ptoDays),
      weeklyOvertime: this.toNumber(row.dataset.weeklyOvertime)
    }
  }

  storeRowMetrics(row, metrics) {
    row.dataset.total = metrics.total
    row.dataset.overtime = metrics.overtime
    row.dataset.night = metrics.night
    row.dataset.holiday = metrics.holiday
    row.dataset.ptoTime = metrics.ptoTime
    row.dataset.ptoDays = metrics.ptoDays
    row.dataset.weeklyOvertime = metrics.weeklyOvertime
  }

  /**
   * Updates the summaryTotals object by applying the difference between two summary objects.
   * @param {Object} previous - The previous summary object containing totals.
   * @param {Object} next - The next summary object containing totals.
   */
  applySummaryDelta(previous, next) {
    this.summaryTotals.total += next.total - previous.total
    this.summaryTotals.overtime += next.overtime - previous.overtime
    this.summaryTotals.night += next.night - previous.night
    this.summaryTotals.holiday += next.holiday - previous.holiday
    this.summaryTotals.ptoTime += next.ptoTime - previous.ptoTime
    this.summaryTotals.ptoDays += next.ptoDays - previous.ptoDays
    this.summaryTotals.weeklyOvertime += next.weeklyOvertime - previous.weeklyOvertime
  }

  renderSummary() {
    const sumNormal = document.getElementById("sumNormal")
    const sumOvertime = document.getElementById("sumOvertime")
    const sumNight = document.getElementById("sumNight")
    const sumHoliday = document.getElementById("sumHoliday")
    const sumPtoTime = document.getElementById("sumPtoTime")
    const sumPtoDays = document.getElementById("sumPtoDays")
    const sumWeeklyOvertime = document.getElementById("sumWeeklyOvertime")

    if (sumNormal) sumNormal.textContent = this.formatTime(this.summaryTotals.total)
    if (sumOvertime) sumOvertime.textContent = this.formatTime(this.summaryTotals.overtime)
    if (sumNight) sumNight.textContent = this.formatTime(this.summaryTotals.night)
    if (sumHoliday) sumHoliday.textContent = this.formatTime(this.summaryTotals.holiday)
    if (sumPtoTime) sumPtoTime.textContent = this.formatTime(this.summaryTotals.ptoTime)
    if (sumPtoDays) sumPtoDays.textContent = this.formatDays(this.summaryTotals.ptoDays)
    if (sumWeeklyOvertime) sumWeeklyOvertime.textContent = this.formatTime(this.summaryTotals.weeklyOvertime)
  }

  calculateOneDay({ startTime, endTime, isHoliday, normalWorkMinutes, breakTimes }) {
    if (!startTime || !endTime) return { total: 0, overtime: 0, night: 0, holiday: 0 };
    if (startTime === null || endTime === null) return { total: 0, overtime: 0, night: 0, holiday: 0 };

    if (endTime < startTime) endTime += 1440; //日またぎ
    const totalWorkMinutes = this.getTotalWorkMinutes(startTime, endTime, breakTimes);
    const night = this.calculateNightTime(startTime, endTime);
    const overtime = isHoliday ? 0 : Math.max(0, totalWorkMinutes - normalWorkMinutes);
    const holiday = isHoliday ? totalWorkMinutes : 0;
    return { total: totalWorkMinutes, overtime, night, holiday };
  }

  getTotalWorkMinutes(start, end, breakTimes) {
    const startMin = this.toMinutes(start);
    const endMin = this.toMinutes(end)
    let totalMin = endMin - startMin;
    breakTimes.forEach(b => {
      const breakStart = this.toMinutes(b.start);
      const breakEnd = this.toMinutes(b.end);
      if (breakStart < endMin && breakEnd > startMin) {
        const overlapStart = Math.max(startMin, breakStart);
        const overlapEnd = Math.min(endMin, breakEnd);
        totalMin -= (overlapEnd - overlapStart);
      }
    });
    return totalMin;
  }

  calculateNightTime(startTime, endTime) {
    let s = this.toMinutes(startTime);
    let e = this.toMinutes(endTime);
    if (e < s) e += 1440; // handle overnight

    let nightStart = 22 * 60; // 22:00
    let nightEnd = 29 * 60;   // 05:00 next day (29:00)

    let totalNight = 0;

    for (let t = s; t < e; t++) {
      let t24 = t % 1440;
      if (t24 >= nightStart || t24 < (nightEnd % 1440)) {
        totalNight++;
      }
    }
    return totalNight;
  }

  getPaidTimeOffMinutes(vacationPattern) {
    if (!vacationPattern?.name) return 0
    const base = vacationPattern.time_slice?.duration || 0

    if (vacationPattern.name === "有休(1日)") return base
    if (vacationPattern.name === "有休(午前)" || vacationPattern.name === "有休(午後)") return Math.floor(base / 2)

    return 0
  }

  getPaidTimeOffDays(ptoTime, vacationPattern) {
    const oneDayMinutes = vacationPattern?.time_slice?.duration || 0
    if (!oneDayMinutes || !ptoTime) return 0
    return ptoTime / oneDayMinutes
  }

  toHHMM(minutes) {
    if (minutes == null) return "";
    const h = Math.floor(minutes / 60).toString().padStart(2, "0");
    const m = (minutes % 60).toString().padStart(2, "0");
    return `${h}:${m}`;
  }

  toNumber(value) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  formatDays(value) {
    if (!value) return "0日"
    return Number.isInteger(value) ? `${value}日` : `${value.toFixed(1)}日`
  }

  formatTime(min) {
    if (!min || min <= 0) return "0時間0分";
    const h = Math.floor(min / 60), m = min % 60;
    return `${h}時間${m}分`;
  }

  toMinutes(timeStr) {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(":").map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }

  calculateWeeklyOvertime(row, currentRowTotal = null) {
    const rowDate = row.dataset.date
    if (!rowDate) return 0

    const currentWeekStart = this.getWeekStart(new Date(rowDate))
    let weeklyTotalMinutes = 0

    this.dayRowTargets.forEach((r) => {
      const dateStr = r.dataset.date
      if (!dateStr) return

      const rDate = new Date(dateStr)
      const rWeekStart = this.getWeekStart(rDate)

      if (rWeekStart.getTime() === currentWeekStart.getTime()) {
        const totalMinutes = r === row && currentRowTotal !== null ? currentRowTotal : this.toNumber(r.dataset.total)
        weeklyTotalMinutes += totalMinutes
      }
    })

    if (weeklyTotalMinutes <= 40 * 60) return 0

    return weeklyTotalMinutes - (40 * 60)
  }

  getWeekStart(date) {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    return new Date(d.setDate(diff))
  }

  initializeChoices(el) {
    el.querySelectorAll(".js-choice").forEach((select) => {
      if (this.choicesMap.has(select)) return
      const choices = new Choices(select, {
        itemSelectText: '',
        noResultsText: '見つかりません',
        shouldSort: false
      });
      this.choicesMap.set(select, choices)
    });
  }
}
